import { useEffect, useMemo, useState } from 'react';
import { ConditionType, defaultOperator, Filter, getExpression } from '@fabric-ui/components';
import { FormatterType, isNumberType } from '@fabric-ui/components/Table';
import useQueryObject from '../../../../hooks/useQueryObjectSql';
import { defaultDrillDownHierarchy, OTHERS } from '../../constants';
import { shouldShowTotals } from '../../CustomDashboardsUtils';
import { useGetFieldType } from '../../hooks/useGetFieldType';
import { AllTypeWidgets, Custom, DwQueryRequest, SortBy } from '../../types';

const getOperator = (value: string | null | object, type?: string) => {
  if (value === null) {
    return 'empty';
  }

  return type === FormatterType.date ? defaultOperator[ConditionType.date] : 'equals';
};

const getExpressionValue = (value: string | null | Record<PropertyKey, unknown>, type?: string) => {
  if (value === null) {
    return {};
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  if (type === FormatterType.date) {
    return { values: [value, value] };
  }
  return value;
};

export const useDrillDownData = ({
  widget,
  enabled,
  currentIndex,
  contextFilter,
}: {
  widget: AllTypeWidgets;
  enabled: boolean;
  currentIndex?: number;
  contextFilter: Filter | undefined;
}) => {
  const request = widget.requests[0];
  const { drillDownHierarchy = defaultDrillDownHierarchy } = widget;
  const { activeIndex: aI, fields, drillDownValues = [], notOthersDrillDownValues = [] } = drillDownHierarchy;
  const activeIndex = currentIndex || aI;
  const getFieldType = useGetFieldType(request.projectionId.name);

  const [drillDownSort, setDrillDownSort] = useState<SortBy>(request.sorting[0]);
  const allDrillDownFields = [{ name: request.select.dims[0]?.name }, ...fields];
  useEffect(() => {
    setDrillDownSort(request.sorting[0]);
  }, [activeIndex]);

  const newFilter: Filter[] = useMemo(
    () =>
      Array.from(Array(activeIndex), (_, i) => {
        const type = getFieldType({ entityFieldKey: allDrillDownFields[i].name }) || 'string';
        const value = drillDownValues[i];
        if (value === OTHERS) {
          return {
            not: {
              or: {
                operands: notOthersDrillDownValues.map(notOthersValue =>
                  getExpression({
                    fieldName: allDrillDownFields[i].name,
                    typeCondition: ConditionType[['percentage', 'number'].includes(type) ? 'number' : type === 'bool' ? 'bool' : 'string'],
                    operator: getOperator(notOthersValue),
                    value: getExpressionValue(notOthersValue),
                  })
                ),
              },
            },
          };
        }

        return getExpression({
          fieldName: allDrillDownFields[i].name,
          typeCondition: ConditionType[isNumberType(type) ? 'number' : type || 'string'],
          operator: getOperator(value, type),
          value: getExpressionValue(value, type),
        });
      }),
    [activeIndex, drillDownValues]
  );

  const isCrossTableView = !!(widget.definition as { custom: Custom }).custom?.crossTableView;

  const newQueryObject: DwQueryRequest = useMemo(() => {
    const select = isCrossTableView
      ? { ...request.select, dims: [...fields.slice(0, activeIndex).reverse(), ...request.select.dims] }
      : { ...request.select, dims: [[...request.select.dims, ...fields][activeIndex]] };
    return {
      ...request,
      top: { groupOthers: request.top?.groupOthers ?? false, size: request.top?.size || 20, offset: 0 },
      select,
      filter: { and: { operands: newFilter } },
      sorting: [...select.metrics, ...select.dims].some(dim => dim?.name === drillDownSort?.name) ? [drillDownSort] : [],
      groupBy: isCrossTableView
        ? [...fields.slice(0, activeIndex).reverse(), ...request.select.dims].map(({ name }) => name)
        : [...([...request.select.dims, ...fields].length ? [[...request.select.dims, ...fields][activeIndex].name] : [])],
    };
  }, [activeIndex, newFilter, drillDownSort, request.top]);
  const showTotals = shouldShowTotals(widget);

  const {
    data,
    totalRowCount: totalRowCountDrillDown,
    totals: totalsDrillDown,
    isLoading: isLoadingDrillDown,
    isRefetching: isRefetchingDrillDown,
    errors: errorsDrillDown,
  } = useQueryObject({
    queryObject: newQueryObject,
    totalRowCount: true,
    contextFilter,
    showTotals,
    options: {
      enabled: enabled && activeIndex !== 0 && !!allDrillDownFields[activeIndex] && drillDownValues.length === activeIndex,
      gcTime: 0,
    },
  });

  return {
    newQueryObject,
    data,
    totalRowCountDrillDown,
    totalsDrillDown,
    isLoadingDrillDown,
    isRefetchingDrillDown,
    errorsDrillDown,
    drillDownSort,
    setDrillDownSort,
    allDrillDownFields,
  };
};
